import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from "date-fns";
import React, { useState } from "react";

interface CustomDateRangePickerProps {
  open: boolean;
  onClose: () => void;
  onApply: (startDate: Date, endDate: Date) => void;
  onApplyShortcut: (shortcut: string) => void;
  onClear: () => void;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  currentTimeRange?: string;
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  open,
  onClose,
  onApply,
  onApplyShortcut,
  onClear: _onClear,
  initialStartDate,
  initialEndDate,
  currentTimeRange
}) => {
  const [dateRange, setDateRange] = useState<DateRange<Date>>([
    initialStartDate || null,
    initialEndDate || null
  ]);

  // Function to calculate date range for shortcuts
  const getShortcutDateRange = (shortcut: string): DateRange<Date> => {
    const today = new Date();

    switch (shortcut) {
      case "today": {
        return [startOfDay(today), endOfDay(today)];
      }
      case "yesterday": {
        const yesterday = subDays(today, 1);
        return [startOfDay(yesterday), endOfDay(yesterday)];
      }
      case "thisWeek": {
        return [startOfWeek(today), endOfWeek(today)];
      }
      case "lastWeek": {
        const lastWeek = subWeeks(today, 1);
        return [startOfWeek(lastWeek), endOfWeek(lastWeek)];
      }
      case "last7Days": {
        return [startOfDay(subDays(today, 6)), endOfDay(today)];
      }
      case "thisMonth": {
        return [startOfMonth(today), endOfMonth(today)];
      }
      case "lastMonth": {
        const lastMonth = subMonths(today, 1);
        return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
      }
      case "last30Days": {
        return [startOfDay(subDays(today, 29)), endOfDay(today)];
      }
      case "allTime": {
        return [null, null];
      }
      default: {
        return [null, null];
      }
    }
  };

  // Reset internal state when initialStartDate or initialEndDate changes
  React.useEffect(() => {
    // If currentTimeRange is a shortcut, show its calculated date range
    if (currentTimeRange && !currentTimeRange.startsWith("custom:")) {
      const shortcutDateRange = getShortcutDateRange(currentTimeRange);
      setDateRange(shortcutDateRange);
    } else {
      // Otherwise, use the provided initial dates (for custom ranges)
      setDateRange([initialStartDate || null, initialEndDate || null]);
    }
  }, [initialStartDate, initialEndDate, currentTimeRange]);

  const shortcuts = [
    { label: "All Time", value: "allTime" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last7Days" },
    { label: "Last 30 Days", value: "last30Days" },
    { label: "This Week", value: "thisWeek" },
    { label: "Last Week", value: "lastWeek" },
    { label: "This Month", value: "thisMonth" },
    { label: "Last Month", value: "lastMonth" }
  ];

  const handleShortcutClick = (shortcut: string) => {
    // Update the visual calendar with the shortcut's date range
    const shortcutRange = getShortcutDateRange(shortcut);
    setDateRange(shortcutRange);

    onApplyShortcut(shortcut);
    onClose();
  };

  const handleSearch = () => {
    const [startDate, endDate] = dateRange;
    if (startDate && endDate) {
      onApply(startDate, endDate);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "auto",
          maxWidth: "90vw",
          borderRadius: "8px"
        }
      }}
    >
      <DialogContent sx={{ p: 1, pb: 0 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Custom Shortcuts */}
          <Box sx={{ width: 120, flexShrink: 0, py: 1 }}>
            <Stack spacing={0.5}>
              {shortcuts.map((shortcut) => (
                <Button
                  key={shortcut.value}
                  variant="text"
                  size="small"
                  onClick={() => handleShortcutClick(shortcut.value)}
                  sx={{
                    textTransform: "none",
                    justifyContent: "flex-start",
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    minHeight: 32,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                      color: "text.primary"
                    }
                  }}
                >
                  {shortcut.label}
                </Button>
              ))}
            </Stack>
          </Box>

          {/* MUI StaticDateRangePicker */}
          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticDateRangePicker
                value={dateRange}
                onChange={setDateRange}
                slotProps={{
                  actionBar: { actions: [] },
                  layout: {
                    sx: {
                      "& .MuiDateRangePickerToolbar-root": {
                        display: "none"
                      }
                    }
                  }
                }}
                calendars={2}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 1.5, py: 1, pt: 0 }}>
        <Button
          onClick={() => setDateRange([null, null])}
          color="secondary"
          sx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            px: 2
          }}
        >
          Clear
        </Button>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            px: 2
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSearch}
          disabled={!dateRange[0] || !dateRange[1]}
          variant="contained"
          color="primary"
          sx={{
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            px: 3
          }}
        >
          Search
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomDateRangePicker;
